import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutline,
  ChecklistRtl,
  DescriptionOutlined,
  EditNote,
  EditOutlined,
  ErrorOutline,
  OpenInNew,
  Replay,
  Undo,
  Visibility,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { AI_TONES } from "../../constants/aiTones";
import { useActiveEditorPresence } from "../../context/ActiveEditorContext";
import {
  acceptAllSuggestions,
  getEditorHandle,
  getPendingReview,
  onEditorEvent,
  onReviewResolved,
  rejectAllSuggestions,
  revealInEditor,
} from "../editorBridge";
import { applyDocEditPlan } from "../clientActions";
import { resetEditChain, wasWithdrawnForRefinement } from "../docEditPlanner";

/**
 * Card status for an editor review outcome.
 * @param {object} payload - review-resolved payload.
 * @returns {string} Card status.
 */
const statusForReviewOutcome = (payload) => {
  if (wasWithdrawnForRefinement(payload?.reviewId)) return "superseded";
  switch (payload?.outcome || payload?.status) {
    case "accepted":
      return "accepted";
    case "rejected":
      return "rejected";
    case "superseded":
      return "superseded";
    case "cancelled":
      // The editor dropped the review without a decision (the document was reloaded or closed).
      return "closed";
    default:
      return "reviewed";
  }
};

/** Shared card frame styled with the design tokens. */
const cardSx = {
  mt: 1,
  p: 1.5,
  border: "1px solid var(--q-border, #E3E0DA)",
  borderRadius: "var(--q-radius-lg, 10px)",
  bgcolor: "var(--q-surface, #FFFFFF)",
  boxShadow: "var(--q-shadow-sm, none)",
};

/**
 * Small status chip.
 * @param {{label: string, tone?: 'success'|'muted'|'danger'|'accent'|'warning'}} props - Chip props.
 * @returns {JSX.Element} Chip.
 */
function StatusChip({ label, tone = "muted" }) {
  const colors = {
    success: ["var(--q-success-soft, #E3F2E8)", "var(--q-success-text, #17603A)"],
    danger: ["var(--q-danger-soft, #FCE8E2)", "var(--q-danger-text, #9C2C14)"],
    accent: ["var(--q-accent-soft, #ECE8FC)", "var(--q-accent-text, #3A28B8)"],
    warning: ["var(--q-warning-soft, #FDEFD6)", "var(--q-warning-text, #8A5A00)"],
    muted: ["var(--q-subtle, #F1EFEB)", "var(--q-muted, #5E5A54)"],
  }[tone] || ["var(--q-subtle, #F1EFEB)", "var(--q-muted, #5E5A54)"];
  return <Chip size="small" label={label} sx={{ height: 22, fontSize: "0.72rem", bgcolor: colors[0], color: colors[1], fontWeight: 500 }} />;
}

/**
 * Link to the entity a card created or changed.
 * @param {{entity: {href: string, name: string, linkLabel?: string}}} props - Entity link data.
 * @returns {JSX.Element|null} Link.
 */
function EntityLink({ entity }) {
  if (!entity?.href) return null;
  return (
    <Link component={RouterLink} to={entity.href} underline="hover" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.8rem", fontWeight: 500 }}>
      {entity.linkLabel || `Open ${entity.name}`}
      <OpenInNew sx={{ fontSize: 14 }} />
    </Link>
  );
}

/**
 * Grounding caption ("Grounded in 12 requirements and 3 meeting notes").
 * @param {{counts: object|null}} props - Grounding counts.
 * @returns {JSX.Element|null} Caption.
 */
function GroundingNote({ counts }) {
  if (!counts) return null;
  const parts = [];
  if (counts.requirements) parts.push(`${counts.requirements} requirement${counts.requirements === 1 ? "" : "s"}`);
  if (counts.sessions) parts.push(`${counts.sessions} meeting note${counts.sessions === 1 ? "" : "s"}`);
  if (counts.files) parts.push(`${counts.files} project file${counts.files === 1 ? "" : "s"}`);
  if (!parts.length) return null;
  return (
    <Typography variant="caption" component="div" sx={{ color: "var(--q-faint, inherit)", mt: 0.5 }}>
      Grounded in {parts.join(", ")}
    </Typography>
  );
}

/**
 * Document edit card: what Inky suggested, with Accept all / Review / Undo wired to the editor.
 *
 * Suggestions live only in the open editor's memory. The card is therefore live only while the
 * editor shows THIS card's review: when the review is gone (the user opened another document,
 * reloaded, or another review such as Regenerate section replaced it) the buttons would act on
 * nothing, or on someone else's review. Such a card says the suggestions were closed without a
 * decision and, when it kept its operations, offers "Show again".
 * @param {object} props - Props.
 * @param {object} props.card - doc_edit card.
 * @param {boolean} props.isLatest - Only the newest edit card controls the live review.
 * @param {() => object|null} props.getEditorRef - Active editor getter.
 * @param {(patch: object) => void} props.onUpdate - Persist a card patch.
 * @returns {JSX.Element} Card.
 */
function DocEditCard({ card, isLatest, getEditorRef, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const [reopenError, setReopenError] = useState(null);
  const [, setReviewTick] = useState(0);
  // Re-render when an editor opens or closes, so the card follows the document on screen.
  const activeMeta = useActiveEditorPresence();
  const handle = getEditorHandle(getEditorRef);
  const meta = activeMeta || (handle && typeof handle.getMeta === "function" ? handle.getMeta() : null);
  const sameSurface = Boolean(handle) && (!meta || (card.documentId ? meta.documentId === card.documentId : card.templateId ? meta.templateId === card.templateId : true));
  const pendingStatus = card.status === "pending_review";
  const pending = pendingStatus && sameSurface ? getPendingReview(handle) : undefined;
  // undefined: the editor cannot report its review (older build), so trust the card as before.
  const showsThisReview =
    pending === undefined ? true : card.reviewId ? pending?.reviewId === card.reviewId : Boolean(pending);
  const live = pendingStatus && isLatest && sameSurface && showsThisReview;
  const closed = card.status === "closed" || (pendingStatus && isLatest && sameSurface && !showsThisReview);
  const canShowAgain = closed && isLatest && sameSurface && Array.isArray(card.ops) && card.ops.length > 0 && !meta?.readOnly;

  // Follow the editor's reviews while this card may still be on screen (or could come back).
  useEffect(() => {
    if (!handle || !sameSurface || !(pendingStatus || card.status === "closed")) return undefined;
    const bump = () => setReviewTick((tick) => tick + 1);
    const offStarted = onEditorEvent(handle, "review-started", bump);
    const offResolved = onEditorEvent(handle, "review-resolved", bump);
    return () => {
      offStarted?.();
      offResolved?.();
    };
  }, [handle, sameSurface, pendingStatus, card.status]);

  useEffect(() => {
    if (!live) return undefined;
    const unsubscribe = onReviewResolved(handle, (payload) => {
      if (card.reviewId && payload?.reviewId && payload.reviewId !== card.reviewId) return;
      const status = statusForReviewOutcome(payload);
      onUpdate({ status });
      // A refinement keeps its chain; any other resolution ends it.
      if (status !== "superseded") resetEditChain({ documentId: card.documentId, templateId: card.templateId });
    });
    return () => unsubscribe?.();
  }, [live, handle, card.reviewId, card.documentId, card.templateId, onUpdate]);

  const act = (action) => {
    setBusy(true);
    try {
      const ok = action === "accept" ? acceptAllSuggestions(handle) : rejectAllSuggestions(handle);
      if (ok) {
        onUpdate({ status: action === "accept" ? "accepted" : "rejected" });
        resetEditChain({ documentId: card.documentId, templateId: card.templateId });
      }
    } finally {
      setBusy(false);
    }
  };

  /** Put the card's suggestions back into the editor as a new review. */
  const showAgain = () => {
    setBusy(true);
    setReopenError(null);
    try {
      const outcome = applyDocEditPlan(
        { documentId: card.documentId, templateId: card.templateId, ops: card.ops, label: card.label || "Inky's edit" },
        getEditorRef
      );
      if (!outcome.ok) {
        setReopenError(outcome.error || "The suggestions could not be shown again.");
        return;
      }
      onUpdate({
        status: "pending_review",
        reviewId: outcome.reviewId || null,
        changeCount: outcome.changeCount,
        failedCount: outcome.failedCount || 0,
        ...(outcome.firstBlockId ? { firstBlockId: outcome.firstBlockId } : {}),
      });
      revealInEditor(getEditorHandle(getEditorRef), outcome.firstBlockId || card.firstBlockId);
    } finally {
      setBusy(false);
    }
  };

  const statusChip = closed ? (
    <StatusChip label="Closed" />
  ) : (
    {
      accepted: <StatusChip label="Accepted" tone="success" />,
      rejected: <StatusChip label="Undone" />,
      reviewed: <StatusChip label="Reviewed in the editor" tone="success" />,
      superseded: <StatusChip label="Refined below" tone="accent" />,
      failed: <StatusChip label="Not applied" tone="danger" />,
    }[card.status]
  );

  const title = card.mode === "draft" ? "Draft ready for review" : `${card.changeCount || 0} suggested change${card.changeCount === 1 ? "" : "s"}`;

  let footer = null;
  if (live) {
    footer = (
      <Box sx={{ display: "flex", gap: 1, mt: 1.25, flexWrap: "wrap" }}>
        <Button size="small" variant="contained" disableElevation startIcon={<CheckCircleOutline />} disabled={busy} onClick={() => act("accept")}>
          Accept all
        </Button>
        <Button size="small" variant="outlined" startIcon={<Visibility />} onClick={() => revealInEditor(handle, card.firstBlockId)}>
          Review
        </Button>
        <Button size="small" variant="text" color="inherit" startIcon={<Undo />} disabled={busy} onClick={() => act("reject")}>
          Undo
        </Button>
      </Box>
    );
  } else if (closed) {
    footer = (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" component="div" sx={{ color: "var(--q-faint, inherit)" }}>
          These suggestions were closed without a decision.
          {!canShowAgain && (Array.isArray(card.ops) && card.ops.length ? (sameSurface ? "" : " Open the document to show them again.") : " Ask Inky to make the edit again.")}
        </Typography>
        {canShowAgain && (
          <Button size="small" variant="outlined" startIcon={<Replay />} disabled={busy} onClick={showAgain} sx={{ mt: 0.75 }}>
            Show again
          </Button>
        )}
        {reopenError && (
          <Typography variant="caption" component="div" sx={{ color: "var(--q-danger-text, #9C2C14)", mt: 0.5 }}>
            {reopenError}
          </Typography>
        )}
      </Box>
    );
  } else if (pendingStatus) {
    footer = (
      <Typography variant="caption" component="div" sx={{ color: "var(--q-faint, inherit)", mt: 1 }}>
        {!sameSurface
          ? Array.isArray(card.ops) && card.ops.length
            ? "These suggestions closed with the document. Open it to show them again."
            : "These suggestions closed with the document. Ask Inky to make the edit again."
          : "Superseded by a newer edit."}
      </Typography>
    );
  }

  return (
    <Box sx={cardSx} role="group" aria-label="Document edit">
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <EditNote sx={{ fontSize: 18, color: "var(--q-accent, #4F3BD6)" }} />
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
          {title}
        </Typography>
        {statusChip}
      </Box>
      {card.summary && (
        <Typography variant="body2" sx={{ color: "var(--q-text-2, inherit)", fontSize: "0.82rem" }}>
          {card.summary}
        </Typography>
      )}
      {card.status === "failed" ? (
        <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-start", mt: 0.75 }}>
          <ErrorOutline sx={{ fontSize: 16, mt: "2px", color: "var(--q-danger, #D63B1F)" }} />
          <Typography variant="caption" sx={{ color: "var(--q-text-2, inherit)" }}>
            {card.outcome || card.failedReasons?.[0] || "The editor could not apply these changes."} Ask Inky to try again, or make a narrower edit.
          </Typography>
        </Box>
      ) : (
        card.failedCount > 0 && (
          <Typography variant="caption" component="div" sx={{ color: "var(--q-warning-text, inherit)", mt: 0.5 }}>
            {card.failedCount} change{card.failedCount === 1 ? "" : "s"} could not be placed and {card.failedCount === 1 ? "was" : "were"} skipped.
          </Typography>
        )
      )}
      {card.needsInput?.length > 0 && card.status !== "failed" && (
        <Box sx={{ mt: 0.75 }}>
          <Typography variant="caption" component="div" sx={{ color: "var(--q-muted, inherit)", mb: 0.25 }}>
            Needs your input:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {card.needsInput.slice(0, 8).map((item) => (
              <StatusChip key={item} label={item.length > 48 ? `${item.slice(0, 47)}…` : item} tone="warning" />
            ))}
          </Box>
        </Box>
      )}
      <GroundingNote counts={card.grounding} />
      {footer}
    </Box>
  );
}

/**
 * Keyboard loop for card forms: Enter in a single-line field commits, Esc cancels.
 * @param {() => void} onCommit - Commit handler.
 * @param {() => void} onCancel - Cancel handler.
 * @returns {(event: KeyboardEvent) => void} keydown handler.
 */
const formKeys = (onCommit, onCancel) => (event) => {
  if (event.key === "Escape") {
    event.preventDefault();
    event.stopPropagation();
    onCancel();
  } else if (event.key === "Enter" && !event.shiftKey && event.target?.tagName === "INPUT") {
    event.preventDefault();
    onCommit();
  }
};

/**
 * Outcome view shared by approval cards once decided.
 * @param {{card: object}} props - Card.
 * @returns {JSX.Element} Outcome.
 */
function CardOutcome({ card, onResolve }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (card.status === "cancelled") {
    return <StatusChip label="Cancelled" />;
  }
  if (card.status === "undone") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <StatusChip label="Undone" />
        {card.outcome && (
          <Typography variant="caption" sx={{ color: "var(--q-text-2, inherit)" }}>
            {card.outcome}
          </Typography>
        )}
      </Box>
    );
  }
  const undo = async () => {
    if (busy || !onResolve) return;
    setBusy(true);
    setError(null);
    const result = await onResolve(card.id, "undo");
    setBusy(false);
    if (!result?.ok) setError(result?.error || "That could not be undone.");
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <StatusChip label="Done" tone="success" />
        {card.outcome && (
          <Typography variant="caption" sx={{ color: "var(--q-text-2, inherit)" }}>
            {card.outcome}
          </Typography>
        )}
        {card.undoable && onResolve && (
          <Button size="small" variant="text" color="inherit" startIcon={busy ? <CircularProgress size={12} color="inherit" /> : <Undo />} disabled={busy} onClick={undo} sx={{ ml: "auto", minWidth: 0 }}>
            Undo
          </Button>
        )}
      </Box>
      <EntityLink entity={card.entity} />
      {error && (
        <Typography variant="caption" sx={{ color: "var(--q-danger-text, #9C2C14)" }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Confirmation card for creating a customer or project (edit-before-approve).
 * @param {{card: object, onResolve: Function}} props - Props.
 * @returns {JSX.Element} Card.
 */
function ConfirmCard({ card, onResolve }) {
  const [values, setValues] = useState(card.values || {});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const decided = card.status === "done" || card.status === "cancelled" || card.status === "undone";
  const missing = (card.fields || []).some((field) => field.required && !String(values[field.key] || "").trim());

  const run = async (action) => {
    if (busy || (action === "commit" && missing)) return;
    setBusy(true);
    setError(null);
    const result = await onResolve(card.id, action, values);
    setBusy(false);
    if (!result?.ok) setError(result?.error || "That did not work. Try again.");
  };

  return (
    <Box sx={cardSx} role="group" aria-label={card.title}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {card.title}
      </Typography>
      {card.subtitle && (
        <Typography variant="caption" component="div" sx={{ color: "var(--q-muted, inherit)" }}>
          {card.subtitle}
        </Typography>
      )}
      {decided ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ fontSize: "0.82rem", mb: 0.5 }}>
            {values.name}
          </Typography>
          <CardOutcome card={card} onResolve={onResolve} />
        </Box>
      ) : (
        <Box component="form" onKeyDown={formKeys(() => run("commit"), () => run("cancel"))} onSubmit={(event) => event.preventDefault()} sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          {(card.fields || []).map((field) => (
            <TextField
              key={field.key}
              size="small"
              label={field.label}
              required={field.required}
              multiline={field.multiline}
              minRows={field.multiline ? 2 : undefined}
              value={values[field.key] || ""}
              onChange={(event) => setValues((prev) => ({ ...prev, [field.key]: event.target.value }))}
              disabled={busy}
            />
          ))}
          {error && (
            <Typography variant="caption" sx={{ color: "var(--q-danger-text, #9C2C14)" }}>
              {error}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" variant="contained" disableElevation disabled={busy || missing} onClick={() => run("commit")} startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}>
              Confirm
            </Button>
            <Button size="small" variant="text" color="inherit" disabled={busy} onClick={() => run("cancel")}>
              Cancel
            </Button>
          </Box>
          <Typography variant="caption" sx={{ color: "var(--q-faint, inherit)" }}>
            Nothing is saved until you confirm.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/**
 * Meeting links: one row per meeting with its target project. Pending cards confirm or cancel;
 * done cards show the result with Undo.
 * @param {{card: object, onResolve: Function}} props - Props.
 * @returns {JSX.Element} Card.
 */
function LinkSessionsCard({ card, onResolve }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const items = Array.isArray(card.items) ? card.items : [];
  const decided = card.status === "done" || card.status === "cancelled" || card.status === "undone";

  const run = async (action) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const result = await onResolve(card.id, action);
    setBusy(false);
    if (!result?.ok) setError(result?.error || "That did not work. Try again.");
  };

  return (
    <Box sx={cardSx} role="group" aria-label={card.title || "Link meetings"}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {card.title || "Link meetings"}
      </Typography>
      <Box component="ul" sx={{ listStyle: "none", m: 0, mt: 0.75, p: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
        {items.map((item) => (
          <Box component="li" key={item.sessionId} sx={{ fontSize: "0.8rem", lineHeight: 1.4 }}>
            {item.href ? (
              <Link component={RouterLink} to={item.href} underline="hover" sx={{ fontWeight: 500 }}>
                {item.session}
              </Link>
            ) : (
              <Box component="span" sx={{ fontWeight: 500 }}>{item.session}</Box>
            )}
            <Box component="span" sx={{ color: "var(--q-muted, inherit)" }}>
              {" → "}
              {item.customer ? `${item.customer} / ` : ""}
              {item.project}
              {item.previousProject ? ` (moved from ${item.previousProject})` : ""}
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ mt: 1 }}>
        {decided ? (
          <CardOutcome card={card} onResolve={onResolve} />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" variant="contained" disableElevation disabled={busy} onClick={() => run("commit")} startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}>
                Link {items.length === 1 ? "meeting" : `${items.length} meetings`}
              </Button>
              <Button size="small" variant="text" color="inherit" disabled={busy} onClick={() => run("cancel")}>
                Cancel
              </Button>
            </Box>
            {error && (
              <Typography variant="caption" sx={{ color: "var(--q-danger-text, #9C2C14)" }}>
                {error}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * Prefilled document generation form (replaces the old regex wizard).
 * @param {{card: object, onResolve: Function, contextData: object}} props - Props.
 * @returns {JSX.Element} Card.
 */
function DocGenerateCard({ card, onResolve, contextData }) {
  const projects = useMemo(() => contextData?.projects || [], [contextData]);
  const templates = useMemo(() => (contextData?.templates || []).filter((t) => t.status !== "draft"), [contextData]);
  const [values, setValues] = useState(card.values || {});
  const [nameTouched, setNameTouched] = useState(Boolean(card.values?.documentName));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const decided = card.status === "done" || card.status === "cancelled";

  const project = projects.find((p) => p.id === values.projectId) || null;
  const template = (contextData?.templates || []).find((t) => t.id === values.templateId) || null;
  const ready = Boolean(project && template && String(values.documentName || "").trim());

  useEffect(() => {
    if (nameTouched || !project || !template) return;
    setValues((prev) => ({ ...prev, documentName: `${project.name} - ${template.name}` }));
  }, [project, template, nameTouched]);

  const run = async (action) => {
    if (busy || (action === "commit" && !ready)) return;
    setBusy(true);
    setError(null);
    const result = await onResolve(card.id, action, values);
    setBusy(false);
    if (!result?.ok) setError(result?.error || "That did not work. Try again.");
  };

  return (
    <Box sx={cardSx} role="group" aria-label="Generate document">
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <DescriptionOutlined sx={{ fontSize: 18, color: "var(--q-accent, #4F3BD6)" }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Generate document
        </Typography>
      </Box>
      {decided ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ fontSize: "0.82rem", mb: 0.5 }}>
            {values.documentName}
          </Typography>
          <CardOutcome card={card} />
        </Box>
      ) : (
        <Box component="form" onKeyDown={formKeys(() => run("commit"), () => run("cancel"))} onSubmit={(event) => event.preventDefault()} sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          <Autocomplete
            size="small"
            options={projects}
            value={project}
            disabled={busy}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_event, option) => setValues((prev) => ({ ...prev, projectId: option?.id || "" }))}
            renderInput={(params) => <TextField {...params} label="Project" required />}
          />
          <Autocomplete
            size="small"
            options={templates}
            value={template}
            disabled={busy}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_event, option) => setValues((prev) => ({ ...prev, templateId: option?.id || "" }))}
            renderInput={(params) => <TextField {...params} label="Template" required />}
          />
          <TextField
            size="small"
            label="Document name"
            required
            value={values.documentName || ""}
            disabled={busy}
            onChange={(event) => {
              setNameTouched(true);
              setValues((prev) => ({ ...prev, documentName: event.target.value }));
            }}
          />
          <TextField
            size="small"
            select
            label="Tone"
            value={values.tone || "The Scholar"}
            disabled={busy}
            onChange={(event) => setValues((prev) => ({ ...prev, tone: event.target.value }))}
          >
            {AI_TONES.map((tone) => (
              <MenuItem key={tone.title} value={tone.title}>
                {tone.frontendTitle}
              </MenuItem>
            ))}
          </TextField>
          {error && (
            <Typography variant="caption" sx={{ color: "var(--q-danger-text, #9C2C14)" }}>
              {error}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" variant="contained" disableElevation disabled={busy || !ready} onClick={() => run("commit")} startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}>
              Generate
            </Button>
            <Button size="small" variant="text" color="inherit" disabled={busy} onClick={() => run("cancel")}>
              Cancel
            </Button>
          </Box>
          <Typography variant="caption" sx={{ color: "var(--q-faint, inherit)" }}>
            Uses the project's requirements. Generation runs in the background.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/** Label and tone per staged requirement action. */
const CHANGE_ACTIONS = {
  add: { label: "New", tone: "success" },
  update: { label: "Update", tone: "accent" },
  delete: { label: "Remove", tone: "danger" },
};

/**
 * Where a staged change came from ("Meeting S-1a2b3c at 00:12:30", "You said").
 * @param {object} source - Change source.
 * @returns {string|null} Text.
 */
const sourceLabel = (source) => {
  if (!source) return null;
  if (source.kind === "user") return "You said";
  const noun = { session: "Meeting", requirement: "Requirement", file: "File" }[source.kind] || "Source";
  return `${noun} ${source.ref}${source.timestamp ? ` at ${source.timestamp}` : ""}`;
};

/**
 * Inline editor for one staged requirement (edit-before-approve: focus lands on the title,
 * Enter in the title saves, Esc cancels; only changed values are kept).
 * @param {{change: object, onSave: (values: object) => void, onCancel: () => void}} props - Props.
 * @returns {JSX.Element} Editor.
 */
function ChangeEditor({ change, onSave, onCancel }) {
  const [values, setValues] = useState({ title: change.title || "", description: change.description || "", priority: change.priority || "Medium" });
  const titleRef = useRef(null);
  useEffect(() => {
    titleRef.current?.focus();
  }, []);
  const valid = Boolean(values.title.trim() && values.description.trim());
  const save = () => {
    if (!valid) return;
    const changed = {};
    if (values.title.trim() !== (change.title || "")) changed.title = values.title.trim();
    if (values.description.trim() !== (change.description || "")) changed.description = values.description.trim();
    if (values.priority !== (change.priority || "Medium")) changed.priority = values.priority;
    onSave(changed);
  };
  return (
    <Box
      component="form"
      onSubmit={(event) => event.preventDefault()}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          onCancel();
        }
      }}
      sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: 0.75 }}
    >
      <TextField
        size="small"
        label="Title"
        value={values.title}
        inputRef={titleRef}
        onChange={(event) => setValues((prev) => ({ ...prev, title: event.target.value }))}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            save();
          }
        }}
      />
      <TextField
        size="small"
        label="Description"
        multiline
        minRows={2}
        maxRows={8}
        value={values.description}
        onChange={(event) => setValues((prev) => ({ ...prev, description: event.target.value }))}
      />
      <TextField
        size="small"
        select
        label="Priority"
        value={values.priority}
        onChange={(event) => setValues((prev) => ({ ...prev, priority: event.target.value }))}
        sx={{ maxWidth: 180 }}
      >
        {["Critical", "High", "Medium", "Low"].map((priority) => (
          <MenuItem key={priority} value={priority}>
            {priority}
          </MenuItem>
        ))}
      </TextField>
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button size="small" variant="contained" disableElevation disabled={!valid} onClick={save}>
          Save
        </Button>
        <Button size="small" variant="text" color="inherit" onClick={onCancel}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
}

/**
 * One staged requirement row.
 * @param {object} props - Props.
 * @returns {JSX.Element} Row.
 */
function ChangeRow({ change, merged, result, decided, busy, leftOut, editing, onToggle, onEdit, onSave, onCancelEdit }) {
  const meta = CHANGE_ACTIONS[change.action] || CHANGE_ACTIONS.add;
  const provenance = [
    sourceLabel(change.source),
    change.sessionName && change.source?.kind !== "session" ? `from ${change.sessionName}` : null,
  ].filter(Boolean).join(" · ");
  return (
    <Box
      component="li"
      sx={{
        border: "1px solid var(--q-border-subtle, #ECE9E4)",
        borderRadius: "var(--q-radius-md, 8px)",
        p: 1,
        opacity: leftOut ? 0.55 : 1,
        bgcolor: "var(--q-surface, #FFFFFF)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
        {!decided && (
          <Checkbox
            size="small"
            checked={!leftOut}
            disabled={busy}
            onChange={onToggle}
            inputProps={{ "aria-label": `Include ${merged.title}` }}
            sx={{ p: 0.25, mt: "-2px" }}
          />
        )}
        {decided && result && (result.ok ? (
          <CheckCircleOutline sx={{ fontSize: 16, mt: "2px", color: "var(--q-success, #1B7F45)" }} />
        ) : (
          <Tooltip title={result.error || "Failed"}>
            <ErrorOutline sx={{ fontSize: 16, mt: "2px", color: "var(--q-danger, #D63B1F)" }} />
          </Tooltip>
        ))}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            <StatusChip label={meta.label} tone={meta.tone} />
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontSize: "0.82rem", textDecoration: change.action === "delete" ? "line-through" : "none", overflowWrap: "anywhere" }}
            >
              {merged.title}
            </Typography>
            {merged.priority && change.action !== "delete" && (
              <Typography variant="caption" sx={{ color: "var(--q-muted, inherit)" }}>
                {merged.priority}
              </Typography>
            )}
          </Box>
          {change.action !== "delete" && merged.description && !editing && (
            <Typography
              variant="caption"
              component="div"
              sx={{ color: "var(--q-text-2, inherit)", mt: 0.25, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}
            >
              {merged.description}
            </Typography>
          )}
          {change.action === "update" && change.before?.title && change.before.title !== merged.title && (
            <Typography variant="caption" component="div" sx={{ color: "var(--q-muted, inherit)" }}>
              Was: {change.before.title}
            </Typography>
          )}
          {(provenance || change.source?.quote) && (
            <Typography variant="caption" component="div" sx={{ color: "var(--q-faint, inherit)", mt: 0.25 }}>
              {provenance}
              {change.source?.quote ? `${provenance ? " · " : ""}"${change.source.quote}"` : ""}
            </Typography>
          )}
          {editing && <ChangeEditor change={merged} onCancel={onCancelEdit} onSave={onSave} />}
        </Box>
        {!decided && change.action !== "delete" && !editing && (
          <Tooltip title="Edit before approving">
            <IconButton size="small" aria-label={`Edit ${merged.title}`} disabled={busy} onClick={onEdit}>
              <EditOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

/**
 * Staged requirement changes, grouped in one card (Flint draft groups): every row shows what
 * changes and where it came from; rows can be edited or left out; Approve applies the rest.
 * @param {{card: object, onResolve: Function}} props - Props.
 * @returns {JSX.Element} Card.
 */
function RequirementChangesCard({ card, onResolve }) {
  const [excluded, setExcluded] = useState(() => new Set());
  const [edits, setEdits] = useState({});
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const decided = card.status === "done" || card.status === "cancelled";
  const changes = card.changes || [];
  const resultByKey = new Map((card.results || []).map((result) => [result.key, result]));
  const selected = changes.filter((change) => !excluded.has(change.key)).length;
  const shown = expanded || changes.length <= 6 ? changes : changes.slice(0, 5);
  const counts = changes.reduce((acc, change) => ({ ...acc, [change.action]: (acc[change.action] || 0) + 1 }), {});
  const headline = [
    counts.add ? `${counts.add} new` : null,
    counts.update ? `${counts.update} updated` : null,
    counts.delete ? `${counts.delete} removed` : null,
  ].filter(Boolean).join(", ");

  const run = async (action) => {
    if (busy || (action === "commit" && !selected)) return;
    setBusy(true);
    setError(null);
    const values = action === "commit"
      ? { changes: Object.entries(edits).map(([key, patch]) => ({ key, ...patch })), excluded: [...excluded] }
      : undefined;
    const result = await onResolve(card.id, action, values);
    setBusy(false);
    if (!result?.ok) setError(result?.error || "That did not work. Try again.");
  };

  const toggle = (key) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <Box sx={cardSx} role="group" aria-label="Requirement changes">
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ChecklistRtl sx={{ fontSize: 18, color: "var(--q-accent, #4F3BD6)" }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Requirement changes{headline ? `: ${headline}` : ""}
          </Typography>
          {card.projectName && (
            <Typography variant="caption" component="div" sx={{ color: "var(--q-muted, inherit)" }}>
              {card.projectName}
            </Typography>
          )}
        </Box>
        {card.status === "done" && <StatusChip label="Applied" tone="success" />}
        {card.status === "cancelled" && <StatusChip label="Discarded" />}
      </Box>
      {card.summary && (
        <Typography variant="body2" sx={{ color: "var(--q-text-2, inherit)", fontSize: "0.82rem", mt: 0.5 }}>
          {card.summary}
        </Typography>
      )}
      <Box component="ul" sx={{ listStyle: "none", m: 0, mt: 1, p: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
        {shown.map((change) => (
          <ChangeRow
            key={change.key}
            change={change}
            merged={{ ...change, ...(edits[change.key] || {}) }}
            result={resultByKey.get(change.key)}
            decided={decided}
            busy={busy}
            leftOut={excluded.has(change.key)}
            editing={editing === change.key}
            onToggle={() => toggle(change.key)}
            onEdit={() => setEditing(change.key)}
            onCancelEdit={() => setEditing(null)}
            onSave={(patch) => {
              setEdits((prev) => ({ ...prev, [change.key]: { ...(prev[change.key] || {}), ...patch } }));
              setEditing(null);
            }}
          />
        ))}
      </Box>
      {changes.length > shown.length && (
        <Button size="small" variant="text" color="inherit" onClick={() => setExpanded(true)} sx={{ mt: 0.5, color: "var(--q-muted, inherit)" }}>
          Show all {changes.length}
        </Button>
      )}
      {error && (
        <Typography variant="caption" component="div" sx={{ color: "var(--q-danger-text, #9C2C14)", mt: 0.75 }}>
          {error}
        </Typography>
      )}
      {decided ? (
        <Box sx={{ mt: 1 }}>
          <CardOutcome card={card} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 1, mt: 1.25, flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="contained"
              disableElevation
              disabled={busy || !selected || editing !== null}
              onClick={() => run("commit")}
              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutline />}
            >
              {selected === changes.length ? "Approve all" : `Approve ${selected}`}
            </Button>
            <Button size="small" variant="text" color="inherit" disabled={busy} onClick={() => run("cancel")}>
              Discard
            </Button>
          </Box>
          <Typography variant="caption" component="div" sx={{ color: "var(--q-faint, inherit)", mt: 0.5 }}>
            Nothing is saved until you approve. Untick a row to leave it out.
          </Typography>
        </>
      )}
    </Box>
  );
}

/**
 * Read evidence as a compact linked list (the rows the reply is about).
 * @param {{card: object}} props - Card.
 * @returns {JSX.Element|null} Card.
 */
function EntityListCard({ card }) {
  const items = card.items || [];
  if (!items.length) return null;
  return (
    <Box sx={{ ...cardSx, p: 1 }} role="group" aria-label={card.title}>
      <Typography variant="caption" component="div" sx={{ color: "var(--q-muted, inherit)", fontWeight: 600, px: 0.5, mb: 0.25 }}>
        {card.title}
        {card.total > items.length ? ` · ${items.length} of ${card.total}` : ""}
      </Typography>
      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
        {items.map((item) => (
          <Box component="li" key={`${item.kind}-${item.id}`}>
            <Link
              component={RouterLink}
              to={item.href}
              underline="none"
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 1,
                px: 0.5,
                py: 0.4,
                borderRadius: "var(--q-radius-sm, 6px)",
                color: "var(--q-text, inherit)",
                fontSize: "0.8rem",
                "&:hover": { bgcolor: "var(--q-hover, rgba(0,0,0,0.04))" },
              }}
            >
              <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {item.name}
              </Box>
              {item.meta && (
                <Box component="span" sx={{ color: "var(--q-faint, inherit)", fontSize: "0.72rem", flexShrink: 0 }}>
                  {item.meta}
                </Box>
              )}
            </Link>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Render one saved result card.
 * @param {object} props - Props.
 * @param {object} props.card - Card from tool_call.cards.
 * @param {boolean} props.isLatestEdit - Whether this is the newest doc_edit card in the thread.
 * @param {(cardId: string, action: string, values?: object) => Promise<{ok: boolean, error?: string}>} props.onResolve - Card action handler.
 * @param {object} props.contextData - Entity data for pickers.
 * @param {() => object|null} props.getEditorRef - Active editor getter.
 * @returns {JSX.Element|null} Card.
 */
export default function ResultCard({ card, isLatestEdit, onResolve, contextData, getEditorRef }) {
  const updateCard = React.useCallback((patch) => onResolve(card.id, "update", patch), [card.id, onResolve]);
  switch (card?.kind) {
    case "doc_edit":
      return <DocEditCard card={card} isLatest={isLatestEdit} getEditorRef={getEditorRef} onUpdate={updateCard} />;
    case "confirm":
      return <ConfirmCard card={card} onResolve={onResolve} />;
    case "link_sessions":
      return <LinkSessionsCard card={card} onResolve={onResolve} />;
    case "doc_generate":
      return <DocGenerateCard card={card} onResolve={onResolve} contextData={contextData} />;
    case "requirement_changes":
      return <RequirementChangesCard card={card} onResolve={onResolve} />;
    case "entity_list":
      return <EntityListCard card={card} />;
    default:
      return null;
  }
}
